/** @format */

import { useContext, useState } from 'react';
import { Box, Tabs, Tab, Typography, Container, Popper } from '@mui/material';
import { COMPONENT_TITLE_SX } from '../homeStyles';
import { HomeContext } from '../Home';
import KPICard from './KPICard';
import style from './kpi.module.css';
import { TouchApp } from '@mui/icons-material';

const TABS = ['TODAY', 'MONTH TO DATE', 'YEAR TO DATE'];

export const TABS_MAPPING = {
  TODAY: 'td',
  'MONTH TO DATE': 'mtd',
  'YEAR TO DATE': 'ytd',
};

const KPI_ROWS = [
  {
    rowName: 'REVENUE',
    cells: [
      { title: 'Total Rev', id: 'total_revenue' },
      { title: 'TrevPAR', id: 'trevpar' },
    ],
  },
  {
    rowName: 'ROOMS',
    cells: [
      { title: 'Room Nights Sold', id: 'rooms' },
      { title: 'ARR', id: 'arr' },
      { title: 'Room Rev', id: 'room_revenue' },
      { title: 'Occ %', id: 'occ' },
      { title: 'RevPAR', id: 'revpar' },
    ],
  },
  {
    rowName: 'F&B',
    cells: [
      { title: 'Covers Sold', id: 'covers' },
      { title: 'APC Food', id: 'food_apc' },
      { title: 'APC Bev', id: 'bev_apc' },
      { title: 'Other F&B Rev', id: 'other_fnb_revenue' },
      { title: 'Total F&B Rev', id: 'fnb_revenue' },
    ],
  },
  {
    rowName: 'OOD',
    cells: [
      { title: 'Spa Rev', id: 'spa_revenue' },
      { title: 'MOD Rev', id: 'mod_revenue' },
    ],
  },
];

function KPIContainer() {
  const { activeTab, setActiveTab } = useContext(HomeContext);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        px: {
          xs: 0,
          sm: 3,
        },
      }}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="KPI time period tabs"
        sx={{
          minHeight: { xs: 36, sm: 48 },
          '& .MuiTabs-flexContainer': {
            justifyContent: { xs: 'space-between', sm: 'initial' },
          },
          '& .MuiTab-root': {
            minWidth: { xs: 0, sm: 90 },
            minHeight: { xs: 36, sm: 48 },
            px: { xs: 0.5, sm: 2 },
            py: { xs: 0.5, sm: 1.5 },
            fontSize: { xs: '10px', sm: '14px' },
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          },
        }}
      >
        {TABS.map((tab) => (
          <Tab key={tab} label={tab} value={tab} />
        ))}
      </Tabs>

      <Table rows={KPI_ROWS} title="Key Performance Indices vs. Budget" />
    </Container>
  );
}

function Table({ rows, title }) {
  return (
    <Box
      sx={{
        mt: 2,
        flexGrow: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
          }}
        >
          <Tooltip />
        </Box>

        <Typography
          color="text.disabled"
          variant="h6"
          sx={{
            ...COMPONENT_TITLE_SX,
            textAlign: 'center',
            flexGrow: 1,
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {title}
        </Typography>

        <Typography
          fontSize={12}
          sx={{
            position: 'absolute',
            right: 0,
            display: {
              xs: 'none',
              sm: 'block',
            },
          }}
          color="text.secondary"
        >
          Revenue in &#39;000
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',

          gap: {
            xs: 2,
            sm: 0,
          },
        }}
      >
        {rows.map((row, index) => (
          <Box
            key={index}
            className={style.row}
            aria-label={row.rowName}
            sx={{
              position: 'relative',

              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'stretch',

              border: '1px solid silver',
              p: 1,
              borderRadius: 2,

              pl: {
                xs: '38px',
                sm: 1,
              },

              '&::before': {
                content: '""',

                position: 'absolute',

                display: {
                  xs: 'block',
                  sm: 'none',
                },

                top: '10px',
                bottom: '10px',

                left: '37px',

                width: '1px',

                backgroundColor: '#e0e0e0',

                pointerEvents: 'none',

                zIndex: 1,
              },
            }}
          >
            <Box
              sx={{
                position: 'absolute',

                display: {
                  xs: 'flex',
                  sm: 'none',
                },

                left: 0,
                top: 0,
                bottom: 0,

                width: '38px',

                alignItems: 'center',
                justifyContent: 'center',

                pointerEvents: 'none',

                zIndex: 2,
              }}
            >
              <Typography
                component="div"
                sx={{
                  position: 'absolute',

                  top: '50%',
                  left: '50%',

                  transform: 'translate(-50%, -50%) rotate(-90deg)',

                  whiteSpace: 'nowrap',

                  fontSize: '0.9rem',
                  lineHeight: 1,

                  textAlign: 'center',
                }}
              >
                {row.rowName}
              </Typography>
            </Box>

            <Box
              sx={{
                display: {
                  xs: 'none',
                  sm: 'flex',
                },

                gap: 1,
                alignSelf: 'stretch',
                alignItems: 'center',

                position: 'relative',
              }}
            >
              <Typography
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%) rotate(-90deg)',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  lineHeight: 1,
                  pointerEvents: 'none',
                }}
              >
                {row.rowName}
              </Typography>

              <Box sx={{ width: '20px' }} />

              <Box
                sx={{
                  width: '1px',
                  alignSelf: 'stretch',
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              {row.cells.map((cell, cellIndex) => (
                <KPICard key={cellIndex} kpi={cell} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function Tooltip() {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          color: 'text.disabled',
          cursor: 'pointer',
          zIndex: 1,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <TouchApp
          fontSize="small"
          sx={{
            color: '#757575',
          }}
        />
      </Box>

      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="right-start"
      >
        <Box
          sx={{
            minWidth: 'fit-content',
            backgroundColor: '#e8e4e4',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1,
            zIndex: 1300,
          }}
        >
          <Box sx={{ p: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                mb: 1,
              }}
            >
              KPI Information
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
                mb: 0.5,
              }}
            >
              Color Codes:
            </Typography>

            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                🔴 Red: Below 95%
              </Typography>

              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                🟡 Amber: 95% - 100%
              </Typography>

              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                🟢 Green: Above 100%
              </Typography>
            </Box>
          </Box>
        </Box>
      </Popper>
    </>
  );
}

export default KPIContainer;
